import React from 'react';
import { createPortal } from 'react-dom';
import Hls from 'hls.js';
import { Link, Image } from './common/NextAdapters';
import { Icon } from '@iconify/react';
import { MyListButton } from './my-list-button';
import { RatingButton } from './rating-button';

const EXPAND = 1.34;
const MIN_WIDTH = 300; // poster cards are narrow, so the landscape hover popup must be wider than the card itself
const MAX_WIDTH = 440;
const EDGE = 16;
const PANEL_HEIGHT = 234;
const PLAY_DELAY = 500; // popup sits on the poster this long before the trailer starts

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export function TitlePreview({ title, anchor, previewRef }) {
  const videoRef = React.useRef(null);
  const hlsRef = React.useRef(null);
  const [canPlay, setCanPlay] = React.useState(false);
  const [previewReady, setPreviewReady] = React.useState(false);
  const [muted, setMuted] = React.useState(true);
  const trailerUrl = title ? (title.trailer || title.TrailerUrl) : null;

  // Sit on the poster for a beat after the popup mounts before the trailer starts playing.
  React.useEffect(() => {
    const t = setTimeout(() => setCanPlay(true), PLAY_DELAY);
    return () => clearTimeout(t);
  }, []);

  // Attach (and tear down) the trailer source only once the play-delay has elapsed, so
  // a fleeting hover never opens an HLS connection or downloads video bytes.
  React.useEffect(() => {
    if (!canPlay || !videoRef.current || !trailerUrl) return undefined;
    const video = videoRef.current;
    const isHls = trailerUrl.includes('.m3u8');
    if (isHls && Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      hls.loadSource(trailerUrl);
      hls.attachMedia(video);
      hlsRef.current = hls;
    } else {
      video.src = trailerUrl;
    }
    video.muted = muted;
    video.play().catch(() => {});
    return () => {
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPlay, trailerUrl]);

  React.useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  if (!anchor || !title) return null;

  const id = title.id || title.Id || title.MovieId;
  const name = title.title || title.Title || title.Name;
  const thumbnail = title.thumbnail || title.ThumbnailUrl || title.CoverUrl || title.BannerUrl;
  const categories = Array.isArray(title.categories) && title.categories.length > 0
    ? title.categories.map((c) => c.name).filter(Boolean)
    : [title.category || title.CategoryName || title.Genre].filter(Boolean);
  const year = title.year || title.ReleaseYear;
  const rating = title.rating || title.Rating || title.AgeRating;
  const description = title.description || title.Description || title.Synopsis;

  const width = Math.round(
    Math.min(Math.max(anchor.width * EXPAND, MIN_WIDTH), MAX_WIDTH)
  );
  const artHeight = (width * 9) / 16;
  const height = artHeight + PANEL_HEIGHT;

  const left = clamp(
    anchor.left - (width - anchor.width) / 2,
    EDGE,
    window.innerWidth - width - EDGE
  );
  const top = clamp(
    anchor.top - (artHeight - anchor.height) / 2,
    EDGE,
    Math.max(EDGE, window.innerHeight - height - EDGE)
  );

  return createPortal(
    <div
      ref={previewRef}
      aria-hidden
      style={{ left, top, width }}
      className="fixed z-50 overflow-hidden rounded-xl bg-[#16121f] shadow-2xl shadow-black/80 ring-1 ring-white/15 transition-all duration-200 animate-in fade-in zoom-in-95"
    >
      <div className="relative aspect-video bg-[#110e1b]">
        <Link href={`/movie/${id}`} tabIndex={-1} className="absolute inset-0 block">
          {thumbnail ? (
            <Image
              src={thumbnail}
              alt={name}
              fill
              sizes={`${MAX_WIDTH}px`}
              className="object-cover transition-opacity duration-300"
              style={{ opacity: previewReady ? 0 : 1 }}
            />
          ) : (
            <div className="flex h-full items-center justify-center bg-white/5">
              <Icon icon="ph:film-slate" className="size-8 text-muted-foreground" />
            </div>
          )}
        </Link>

        {trailerUrl && (
          <video
            ref={videoRef}
            className="pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-300"
            style={{ opacity: previewReady ? 1 : 0 }}
            muted={muted}
            loop
            playsInline
            onPlaying={() => setPreviewReady(true)}
          />
        )}

        {previewReady && (
          <button
            type="button"
            onClick={() => setMuted((m) => !m)}
            aria-label={muted ? 'Unmute preview' : 'Mute preview'}
            className="absolute bottom-2.5 right-2.5 z-10 flex size-8 items-center justify-center rounded-full border border-white/30 bg-black/60 text-white backdrop-blur-sm transition-colors duration-200 hover:border-white hover:bg-black/85"
          >
            <Icon icon={muted ? 'ph:speaker-x-fill' : 'ph:speaker-high-fill'} className="size-4" />
          </button>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-center gap-2">
          <Link
            href={`/watch/${id}`}
            aria-label={`Play ${name}`}
            className="flex size-9 items-center justify-center rounded-full bg-white text-black transition-colors hover:bg-white/85"
          >
            <Icon icon="ph:play-fill" className="size-4" />
          </Link>
          <MyListButton title={title} size="sm" />
          <RatingButton title={title} size="sm" />
        </div>

        <h3 className="mt-3 truncate text-base font-bold text-foreground">
          <Link href={`/movie/${id}`} tabIndex={-1} className="hover:text-primary transition-colors">
            {name}
          </Link>
        </h3>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <MetaChip label={year} />
          <MetaChip label={rating} />
          {categories.map((name) => (
            <MetaChip key={name} label={name} />
          ))}
        </div>

        {description && (
          <p className="mt-2.5 line-clamp-3 text-xs leading-5 text-muted-foreground">
            {description}
          </p>
        )}
      </div>
    </div>,
    document.body
  );
}

function MetaChip({ label }) {
  if (!label) return null;
  return (
    <span className="inline-flex items-center rounded-md border border-white/15 bg-white/8 px-1.5 py-0.5 text-[11px] font-medium text-foreground/80">
      {label}
    </span>
  );
}

export default TitlePreview;
